import { DocumentSettingsModal } from '@/components/admin/DocumentSettingsModal';
import {
  FileUploadDetailModal,
  OrphanBadge,
  StatusBadge,
  formatBytes,
  formatDateTime,
} from '@/components/admin/FileUploadDetailModal';
import { Modal } from '@/components/admin/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { formatElapsed } from '@/lib/formatElapsed';
import type {
  AdminFileUploadDeleteOrphansRequest,
  AdminFileUploadListItem,
  FileUploadKind,
  FileUploadStatus,
} from '@innermate/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleStop, Settings } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

type OrphanFilter = '' | 'orphan' | 'active';

interface FilterState {
  ownerEmail: string;
  kind: '' | FileUploadKind;
  status: '' | FileUploadStatus;
  mime: string;
  orphan: OrphanFilter;
  from: string;
  to: string;
}

const EMPTY: FilterState = {
  ownerEmail: '',
  kind: '',
  status: '',
  mime: '',
  orphan: '',
  from: '',
  to: '',
};

const IN_PROGRESS_STATUSES: ReadonlySet<FileUploadStatus> = new Set([
  'uploading',
  'uploaded',
  'queued',
  'processing',
]);

function toIsoOrUndefined(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function formatRelative(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `${sec}초 전`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}분 전`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}시간 전`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}일 전`;
    return formatDateTime(iso);
  } catch {
    return iso;
  }
}

export function AdminFileUploads() {
  const [draft, setDraft] = useState<FilterState>(EMPTY);
  const [filters, setFilters] = useState<FilterState>(EMPTY);
  const [detailOpen, setDetailOpen] = useState<AdminFileUploadListItem | null>(null);
  const [orphanOpen, setOrphanOpen] = useState(false);
  const [orphanResult, setOrphanResult] = useState<{ deleted: number; capped: boolean } | null>(
    null,
  );
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const qc = useQueryClient();

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((prev) => ({ ...prev, ownerEmail: draft.ownerEmail, mime: draft.mime }));
    }, 300);
    return () => clearTimeout(handle);
  }, [draft.ownerEmail, draft.mime]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      kind: draft.kind,
      status: draft.status,
      orphan: draft.orphan,
      from: draft.from,
      to: draft.to,
    }));
  }, [draft.kind, draft.status, draft.orphan, draft.from, draft.to]);

  const ownerEmail = filters.ownerEmail.trim() || undefined;
  const fromIso = useMemo(() => toIsoOrUndefined(filters.from), [filters.from]);
  const toIso = useMemo(() => toIsoOrUndefined(filters.to), [filters.to]);
  const kind = filters.kind || undefined;
  const status = filters.status || undefined;
  const mime = filters.mime.trim() || undefined;
  const orphan = filters.orphan || undefined;

  const query = useInfiniteQuery({
    queryKey: ['admin', 'file-uploads', ownerEmail, kind, status, mime, orphan, fromIso, toIso],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.adminFileUploads({
        ownerEmail,
        kind,
        status,
        mime,
        orphan,
        from: fromIso,
        to: toIso,
        cursor: pageParam,
        limit: 50,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  // 진행 중 현황 — 활성 작업 있으면 자동 갱신 (모니터링).
  const summaryQuery = useQuery({
    queryKey: ['admin', 'file-uploads', 'active-summary'],
    queryFn: api.adminFileUploadActiveSummary,
    refetchInterval: 5000,
  });
  const summary = summaryQuery.data;
  const hasActive = Boolean(summary && summary.uploading + summary.queued + summary.processing > 0);

  const total = query.data?.pages[0]?.total ?? 0;
  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.files) ?? [], [query.data]);

  const invalidateList = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'file-uploads'] });
  };

  // 진행 중 작업이 보이면 목록도 주기적으로 갱신해 상태 전이를 반영.
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => query.refetch(), 5000);
    return () => clearInterval(t);
  }, [hasActive, query]);

  const deleteMut = useMutation({
    mutationFn: (args: { id: string; confirmActive: boolean }) =>
      api.adminDeleteFileUpload(args.id, { confirmActive: args.confirmActive }),
  });

  const cancelMut = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      api.adminCancelFileUpload(args.id, { reason: args.reason }),
    onSuccess: () => {
      invalidateList();
      summaryQuery.refetch();
    },
    onError: (err) =>
      setMutationError(err instanceof ApiError ? err.message : '중지 중 오류가 발생했습니다.'),
  });

  const onCancelRow = (r: AdminFileUploadListItem) => {
    const reason = window.prompt(
      `"${r.originalName}" 처리를 중지합니다. 사용자에게 표시할 사유:`,
      '대용량 파일 사유로 중단되었습니다.',
    );
    if (reason == null) return;
    cancelMut.mutate({ id: r.id, reason: reason.trim() || '대용량 파일 사유로 중단되었습니다.' });
  };

  const deleteOrphansMut = useMutation({
    mutationFn: (body: AdminFileUploadDeleteOrphansRequest) =>
      api.adminDeleteOrphanFileUploads(body),
    onSuccess: (data) => {
      setOrphanResult(data);
      invalidateList();
    },
    onError: (err) => {
      setMutationError(err instanceof ApiError ? err.message : '일괄 삭제 중 오류가 발생했습니다.');
    },
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && query.hasNextPage && !query.isFetchingNextPage) {
          query.fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [query]);

  function reset() {
    setDraft(EMPTY);
    setFilters(EMPTY);
  }

  return (
    <PageShell
      header={
        <>
          <PageHeader
            title="대화 첨부파일"
            count={total}
            description="사용자가 대화에 첨부한 모든 업로드 파일입니다. 엑셀·이미지·PDF·기타 바이너리가 대상이며, 원본 대화가 삭제된 '고아' 파일은 일괄 정리할 수 있습니다."
            actions={
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  className="gap-1.5"
                  onClick={() => setSettingsOpen(true)}
                >
                  <Settings className="size-4" />
                  설정
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setOrphanResult(null);
                    setMutationError(null);
                    setOrphanOpen(true);
                  }}
                >
                  고아 파일 일괄 삭제
                </Button>
                <Button variant="secondary" onClick={() => query.refetch()}>
                  새로고침
                </Button>
              </div>
            }
          />
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <FilterField label="소유자 이메일 (substring)">
              <input
                type="text"
                value={draft.ownerEmail}
                onChange={(e) => setDraft((s) => ({ ...s, ownerEmail: e.target.value }))}
                placeholder="hong@..."
                className="h-9 w-56 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <FilterField label="유형">
              <select
                value={draft.kind}
                onChange={(e) =>
                  setDraft((s) => ({ ...s, kind: e.target.value as FilterState['kind'] }))
                }
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              >
                <option value="">전체</option>
                <option value="excel">Excel</option>
                <option value="document">문서</option>
                <option value="other">기타</option>
              </select>
            </FilterField>
            <FilterField label="상태">
              <select
                value={draft.status}
                onChange={(e) =>
                  setDraft((s) => ({ ...s, status: e.target.value as FilterState['status'] }))
                }
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              >
                <option value="">전체</option>
                <option value="uploading">업로드 중</option>
                <option value="queued">대기 중</option>
                <option value="processing">처리 중</option>
                <option value="ready">완료</option>
                <option value="failed">실패</option>
                <option value="canceled">중지됨</option>
              </select>
            </FilterField>
            <FilterField label="MIME (substring)">
              <input
                type="text"
                value={draft.mime}
                onChange={(e) => setDraft((s) => ({ ...s, mime: e.target.value }))}
                placeholder="image/, pdf, ..."
                className="h-9 w-40 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <FilterField label="대화 연결">
              <select
                value={draft.orphan}
                onChange={(e) =>
                  setDraft((s) => ({ ...s, orphan: e.target.value as OrphanFilter }))
                }
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              >
                <option value="">전체</option>
                <option value="active">살아있는 대화</option>
                <option value="orphan">고아 (대화 없음)</option>
              </select>
            </FilterField>
            <FilterField label="시작">
              <input
                type="datetime-local"
                value={draft.from}
                onChange={(e) => setDraft((s) => ({ ...s, from: e.target.value }))}
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <FilterField label="끝">
              <input
                type="datetime-local"
                value={draft.to}
                onChange={(e) => setDraft((s) => ({ ...s, to: e.target.value }))}
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <Button variant="ghost" size="sm" onClick={reset}>
              초기화
            </Button>
          </div>
        </>
      }
    >
      {query.isError ? (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {query.error instanceof ApiError
            ? query.error.message
            : '첨부파일을 불러오지 못했습니다.'}
        </div>
      ) : null}
      {mutationError ? (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {mutationError}
        </div>
      ) : null}

      {summary ? (
        <div className="mb-4 flex flex-wrap items-center gap-4 rounded-xl border border-border bg-surface px-4 py-3 text-sm">
          <span className="font-medium">현재 진행 현황</span>
          <span className="text-foreground-muted">
            업로드 중 <strong className="text-foreground">{summary.uploading}</strong>
          </span>
          <span className="text-foreground-muted">
            대기 <strong className="text-foreground">{summary.queued}</strong>
          </span>
          <span className="text-foreground-muted">
            변환 중 <strong className="text-foreground">{summary.processing}</strong>
          </span>
          {hasActive ? (
            <span className="text-xs text-foreground-muted">· 5초마다 자동 갱신</span>
          ) : (
            <span className="text-xs text-foreground-muted">· 진행 중인 작업 없음</span>
          )}
        </div>
      ) : null}

      <div className="rounded-2xl border border-border bg-surface overflow-hidden">
        <table className="w-full text-sm table-fixed">
          <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
            <tr>
              <th className="text-left font-medium px-4 py-3 w-[160px]">업로드</th>
              <th className="text-left font-medium px-4 py-3 w-[180px]">소유자</th>
              <th className="text-left font-medium px-4 py-3">파일명</th>
              <th className="text-left font-medium px-4 py-3 w-[120px]">크기/유형</th>
              <th className="text-left font-medium px-4 py-3 w-[120px]">소요</th>
              <th className="text-left font-medium px-4 py-3 w-[160px]">상태</th>
              <th className="text-left font-medium px-4 py-3 w-[180px]">연결</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-foreground-muted py-10">
                  불러오는 중…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-foreground-muted py-10">
                  조건에 맞는 파일이 없습니다.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                // biome-ignore lint/a11y/useKeyWithClickEvents: 행 전체 클릭으로 상세 — 키보드 사용자는 행 안의 액션 버튼으로 접근
                <tr
                  key={r.id}
                  className="border-t border-border hover:bg-surface-muted/30 align-top cursor-pointer"
                  onClick={() => setDetailOpen(r)}
                >
                  <td className="px-4 py-3 whitespace-nowrap" title={formatDateTime(r.createdAt)}>
                    <div>{formatRelative(r.createdAt)}</div>
                    <div className="text-xs text-foreground-muted font-mono">
                      {formatDateTime(r.createdAt)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <div className="truncate">{r.ownerDisplayName ?? '—'}</div>
                      <div className="text-xs text-foreground-muted truncate">
                        {r.ownerEmail ?? ''}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="truncate font-medium" title={r.originalName}>
                      {r.originalName}
                    </div>
                    <div className="text-[11px] text-foreground-muted font-mono truncate">
                      {r.mime || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="font-mono">{formatBytes(r.sizeBytes)}</div>
                    <span className="inline-block mt-1 rounded bg-surface-muted px-2 py-0.5 text-[10px] font-mono uppercase">
                      {r.kind}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div title="MinIO PUT (multipart + storage)">
                      <span className="text-foreground-muted">업로드 </span>
                      {r.uploadMs != null ? formatElapsed(r.uploadMs) : '—'}
                    </div>
                    <div className="mt-1" title="엑셀 파싱 + DB 적재">
                      <span className="text-foreground-muted">분석 </span>
                      {r.status === 'ready' && r.readyAt
                        ? formatElapsed(
                            new Date(r.readyAt).getTime() - new Date(r.createdAt).getTime(),
                          )
                        : '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={r.status} error={r.error} />
                      {IN_PROGRESS_STATUSES.has(r.status) ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCancelRow(r);
                          }}
                          disabled={cancelMut.isPending}
                          title="처리 중지 (사용자에게 사유 표시)"
                          aria-label={`${r.originalName} 처리 중지`}
                          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-foreground-muted hover:text-destructive hover:bg-surface-muted"
                        >
                          <CircleStop className="size-3.5" />
                          중지
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <OrphanBadge item={r} />
                    {r.linkedQueryCount > 0 ? (
                      <div className="mt-1 text-[11px] text-foreground-muted">
                        연결 쿼리 {r.linkedQueryCount}건
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div
        ref={sentinelRef}
        className="h-12 flex items-center justify-center text-xs text-foreground-muted"
      >
        {query.isFetchingNextPage
          ? '더 불러오는 중…'
          : query.hasNextPage
            ? '스크롤 시 자동 로딩'
            : rows.length > 0
              ? '— 끝 —'
              : ''}
      </div>

      {detailOpen ? (
        <FileUploadDetailModal
          item={detailOpen}
          onClose={() => {
            setDetailOpen(null);
            setMutationError(null);
          }}
          onDeleted={() => {
            setDetailOpen(null);
            invalidateList();
          }}
          deleteMut={deleteMut}
        />
      ) : null}

      {settingsOpen ? <DocumentSettingsModal onClose={() => setSettingsOpen(false)} /> : null}

      {orphanOpen ? (
        <OrphanBulkDeleteModal
          filterSnapshot={{ ownerEmail, kind, mime, from: fromIso, to: toIso }}
          result={orphanResult}
          pending={deleteOrphansMut.isPending}
          onClose={() => {
            setOrphanOpen(false);
            setOrphanResult(null);
          }}
          onConfirm={() =>
            deleteOrphansMut.mutate({ ownerEmail, kind, mime, from: fromIso, to: toIso })
          }
        />
      ) : null}
    </PageShell>
  );
}

function OrphanBulkDeleteModal({
  filterSnapshot,
  result,
  pending,
  onClose,
  onConfirm,
}: {
  filterSnapshot: {
    ownerEmail: string | undefined;
    kind: FileUploadKind | undefined;
    mime: string | undefined;
    from: string | undefined;
    to: string | undefined;
  };
  result: { deleted: number; capped: boolean } | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const filtersActive = Boolean(
    filterSnapshot.ownerEmail ||
      filterSnapshot.kind ||
      filterSnapshot.mime ||
      filterSnapshot.from ||
      filterSnapshot.to,
  );
  return (
    <Modal
      open
      onClose={onClose}
      title="고아 파일 일괄 삭제"
      description="어떤 대화에도 연결되지 않은 파일만 삭제합니다."
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            닫기
          </Button>
          {result ? null : (
            <Button variant="danger" disabled={pending} onClick={onConfirm}>
              {pending ? '삭제 중…' : filtersActive ? '현재 필터 범위 삭제' : '전체 고아 삭제'}
            </Button>
          )}
        </div>
      }
    >
      {result ? (
        <div className="space-y-2 text-sm">
          <p>
            <strong>{result.deleted}</strong>건의 고아 파일을 삭제했습니다.
          </p>
          {result.capped ? (
            <p className="text-foreground-muted">
              한 번 호출 상한(1000건)에 걸려 일부만 처리되었습니다. 다시 실행하면 나머지를 이어서
              제거합니다.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <p>
            대상: <code>chat_messages.attached_file_ids</code> 어디에도 나타나지 않는 파일. 업로드
            후 한 번도 메시지에 붙이지 않은 파일 또는 원본 대화가 삭제된 파일.
          </p>
          {filtersActive ? (
            <div className="rounded-md border border-border bg-surface-muted/40 p-3 text-xs">
              <div className="font-medium mb-1">현재 필터</div>
              <ul className="space-y-0.5 font-mono">
                {filterSnapshot.ownerEmail ? <li>· 이메일 ~ {filterSnapshot.ownerEmail}</li> : null}
                {filterSnapshot.kind ? <li>· 유형 = {filterSnapshot.kind}</li> : null}
                {filterSnapshot.mime ? <li>· MIME ~ {filterSnapshot.mime}</li> : null}
                {filterSnapshot.from ? <li>· 시작 ≥ {filterSnapshot.from}</li> : null}
                {filterSnapshot.to ? <li>· 끝 ≤ {filterSnapshot.to}</li> : null}
              </ul>
            </div>
          ) : (
            <p className="text-foreground-muted">
              필터가 없으므로 <strong>전체 고아 파일</strong> 이 대상입니다.
            </p>
          )}
          <p className="text-[12px] text-foreground-muted">
            한 번에 최대 1000건. 각 파일은 스토리지 + DB 에서 완전 삭제됩니다. 재실행으로 이어서
            정리 가능.
          </p>
        </div>
      )}
    </Modal>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: input 자식이 children 으로 전달되는 wrapping label 패턴 (HTML 적법)
    <label className="flex flex-col gap-1">
      <span className="text-xs text-foreground-muted">{label}</span>
      {children}
    </label>
  );
}
